import {computed, ref} from "vue";
import {birthdayGiftApi} from "@/container";
import {VoucherCheckResult} from "@/birthday-gift/interfaces";

export type GiftScanState =
    | 'idle'
    | 'checking'
    | 'unknown'
    | 'found'
    | 'redeeming'
    | 'already'
    | 'expired'
    | 'done';

/**
 * Drives the counter scan dialog: look a code up, then hand the gift over.
 */
const useGiftScan = () => {
    const state = ref<GiftScanState>('idle');
    const result = ref<VoucherCheckResult | null>(null);
    const manualCode = ref('');
    const error = ref<string | null>(null);

    const busy = computed(() => ['checking', 'redeeming'].includes(state.value));

    const reset = () => {
        state.value = 'idle';
        result.value = null;
        manualCode.value = '';
        error.value = null;
    };

    /**
     * Map a lookup outcome to the panel to display.
     */
    const applyResult = (outcome: VoucherCheckResult) => {
        result.value = outcome;

        state.value = ({
            valid: 'found',
            already_redeemed: 'already',
            expired: 'expired',
            unknown: 'unknown',
        } as const)[outcome.status] ?? 'unknown';
    };

    /**
     * Look a code up, whether scanned or typed.
     */
    const check = async (code: string) => {
        // A wedge burst ends with Enter, which also reaches the manual input's
        // own handler: without this guard the same code would be looked up twice.
        if (!code.trim() || state.value === 'checking') {
            return;
        }

        state.value = 'checking';
        error.value = null;

        try {
            applyResult(await birthdayGiftApi().checkCode(code));
        } catch {
            state.value = 'unknown';
            result.value = null;
        }
    };

    /**
     * Hand the gift over. The endpoint answers 409/410/404 when the code is
     * no longer redeemable, and the body carries the reason.
     */
    const redeem = async () => {
        const code = result.value?.code;

        if (!code) {
            return;
        }

        state.value = 'redeeming';
        error.value = null;

        try {
            result.value = await birthdayGiftApi().redeem(code);
            state.value = 'done';
        } catch (failure) {
            const body = (failure as {response?: {data?: {data?: VoucherCheckResult}}})?.response?.data?.data;

            // 409/410/404 carry the reason: show the matching panel.
            if (body) {
                applyResult(body);

                return;
            }

            // Network or server error: the code is still valid, stay on the
            // gift panel so the team can simply try again.
            state.value = 'found';
            error.value = 'La remise n\'a pas pu être enregistrée. Réessayez.';
        }
    };

    return {state, result, manualCode, busy, error, check, redeem, reset};
};

export default useGiftScan;
